import { useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { ROUTES } from '@utils/constants.js'
import { cfIconSvgProps } from '@utils/iconSvg.js'
import { useModeStore } from '@stores/modeStore.js'
import { GlobalSearch } from '@components/search/GlobalSearch.js'
import { NavDrawer } from './NavDrawer.js'
import { ModeToggle } from './ModeToggle.js'
import { getPassoByTopicoId } from '@content/jornada.js'
import { useAlertasCount } from '@hooks/useAlertasCount.js'

const PAGE_TITLES: Record<string, string> = {
  [ROUTES.HOME]: 'Início',
  [ROUTES.PAINEL]: 'Obra',
  [ROUTES.MODULOS]: 'Jornada',
  [ROUTES.ETAPA]: GUIA_LABELS.etapaAtual,
  [ROUTES.FINANCEIRO]: 'Financeiro',
  [ROUTES.DOCUMENTOS]: 'Arquivos da obra',
  [ROUTES.CONFIG]: 'Configurações',
  [ROUTES.DIARIO]: 'Diário de bordo',
  [ROUTES.PENDENCIAS]: 'Tarefas do canteiro',
  [ROUTES.ALERTAS]: 'Alertas',
  [ROUTES.CALCULADORAS]: GUIA_LABELS.ferramentasIndex,
  [ROUTES.CHECKLIST_PENDENTES]: 'Checklists do guia',
  [ROUTES.CONTRATOS]: 'Contratos e pagamentos',
  [ROUTES.CONTATOS]: 'Contatos da obra',
  [ROUTES.CRONOGRAMA]: 'Cronograma',
  [ROUTES.DECISOES]: 'Memorial de decisões',
  [ROUTES.MATERIAIS]: 'Cotações de materiais',
}

function IconMenu({ size = 22 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <path d='M4 7h16M4 12h16M4 17h16' />
    </svg>
  )
}

function IconClose({ size = 22 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)} strokeWidth={2.25}>
      <path d='M6 6l12 12M18 6L6 18' />
    </svg>
  )
}

function IconBell({ size = 22 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <path d='M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' />
      <path d='M10.3 21a2 2 0 0 0 3.4 0' />
    </svg>
  )
}

function IconSearch({ size = 22 }: { size?: number }) {
  return (
    <svg {...cfIconSvgProps(size)}>
      <circle cx='11' cy='11' r='7' />
      <path d='M20 20l-3-3' />
    </svg>
  )
}

export function Header() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { topicoId } = useParams<{ topicoId?: string }>()
  const mode = useModeStore((s) => s.mode)
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const alertas = useAlertasCount()

  const passo = topicoId ? getPassoByTopicoId(topicoId) : undefined
  let title = PAGE_TITLES[pathname] ?? 'Minha Obra'
  if (pathname.startsWith(`${ROUTES.CONTRATOS}/`)) title = 'Contrato'
  if (passo) {
    title = passo.titulo
  }

  return (
    <header className={`cf3-header cf3-header--${mode}`} role='banner'>
      <div className='cf3-header__inner'>
        <div className='cf3-header__left'>
          <button
            type='button'
            className='cf3-header__menu-btn'
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <IconClose /> : <IconMenu />}
          </button>
          <span className='cf3-header__title'>{title}</span>
        </div>

        <div className='cf3-header__actions'>
          <button
            type='button'
            className='cf3-header__btn'
            aria-label='Buscar'
            onClick={() => setSearchOpen(true)}
          >
            <IconSearch />
          </button>
          <button
            type='button'
            className='cf3-header__btn'
            aria-label={alertas > 0 ? `Alertas · ${alertas}` : 'Alertas'}
            onClick={() => navigate(ROUTES.ALERTAS)}
          >
            <span className='cf3-header__icon-wrap' aria-hidden>
              <IconBell />
              {alertas > 0 && (
                <span className='cf3-header__badge'>{alertas > 9 ? '9+' : alertas}</span>
              )}
            </span>
          </button>
          <ModeToggle variant='header' />
        </div>
      </div>
      <NavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  )
}
